/**
 * Private Lock Screen — shown when the viewer cannot see a profile's
 * content. Renders one of three states based on the viewer's
 * relationship to the target:
 *
 *   • 'private'  — private profile, viewer is not following.
 *                  Shows "Send follow request" CTA.
 *   • 'pending'  — viewer's follow request is awaiting approval.
 *                  Shows disabled "Request sent" badge.
 *   • 'blocked'  — viewer has been blocked by target.
 *                  Shows neutral message, no CTA.
 *
 * The state is computed in profile-screen via fetchUserVisibilityState
 * (server-side authoritative) so the UI cannot drift from DB truth.
 */

import React from 'react'
import { Lock, Clock, ShieldBan } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type LockScreenState = 'private' | 'pending' | 'blocked'

interface PrivateLockScreenProps {
  state?: LockScreenState
  onSendRequest?: () => void
  isRequestPending?: boolean
}

export const PrivateLockScreen: React.FC<PrivateLockScreenProps> = ({
  state = 'private',
  onSendRequest,
  isRequestPending = false,
}) => {
  const { t } = useTranslation()

  const config = {
    private: {
      Icon: Lock,
      title: t('profile.privateAccount'),
      description: t('profile.privateAccountDesc'),
    },
    pending: {
      Icon: Clock,
      title: t('profile.privateAccount'),
      description: t('profile.privateAccountPendingDesc'),
    },
    blocked: {
      Icon: ShieldBan,
      title: t('profile.privateAccount'),
      description: t('profile.privateAccountBlockedDesc'),
    },
  }[state]

  const Icon = config.Icon

  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-16 h-16 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-4">
        <Icon size={24} className="text-text-hint" />
      </div>
      <h3 className="text-lg font-display font-bold text-text mb-2">
        {config.title}
      </h3>
      <p className="text-sm text-text-hint max-w-xs leading-relaxed mb-6">
        {config.description}
      </p>

      {state === 'private' && onSendRequest && (
        <button
          type="button"
          onClick={onSendRequest}
          disabled={isRequestPending}
          className="px-5 py-2.5 rounded-full bg-primary text-text-on-primary text-sm font-semibold disabled:opacity-50 transition-opacity"
        >
          {t('profile.sendFollowRequest')}
        </button>
      )}

      {state === 'pending' && (
        <div className="px-5 py-2.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-sm text-text-hint">
          {t('profile.followRequestSent')}
        </div>
      )}
    </div>
  )
}
